import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Seo } from "../components/Seo";
import { useBooking } from "../context/BookingContext";
import api, { formatApiError } from "../lib/api";

const AREAS = [
  { key: "Face", label: "Face & Skin", desc: "Facials, injectables, resurfacing" },
  { key: "Body", label: "Body", desc: "Sculpting, toning, skin tightening" },
  { key: "Hair", label: "Hair", desc: "Restoration & regrowth" },
  { key: "Wellness", label: "Wellness", desc: "Weight loss & vitamin boosts" },
];

const DOWNTIMES = [
  { key: "None", label: "No downtime", desc: "I need to get right back to my day" },
  { key: "A little", label: "A little is fine", desc: "A day or two of redness is okay" },
  { key: "Flexible", label: "I'm flexible", desc: "I can plan around recovery for bigger results" },
];

export default function Quiz() {
  const { openBooking } = useBooking();
  const [concerns, setConcerns] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ concern: "", area: "", downtime: "" });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/concerns").then((r) => setConcerns(r.data)).catch(() => {});
  }, []);

  const totalSteps = 4; // concern, area, downtime, contact
  const progress = Math.round(((results ? totalSteps : step) / totalSteps) * 100);

  const pick = (key, value, advance = true) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    if (advance) setTimeout(() => setStep((s) => s + 1), 150);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/submit", { ...answers, ...contact });
      setResults(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setResults(null);
    setStep(0);
    setAnswers({ concern: "", area: "", downtime: "" });
    setContact({ name: "", email: "", phone: "" });
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 bg-stone min-h-screen" data-testid="quiz-page">
      <Seo title="Treatment Finder" description="Answer a few quick questions and discover the med-spa treatments best suited to your goals at Iridescent Medical & Wellness Spa in Clinton Township, MI." path="/quiz" />
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 text-iri-mid text-xs uppercase tracking-[0.2em]">
            <Sparkles className="h-4 w-4" /> Treatment Finder
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-light text-ink mt-3">
            {results ? "Your personalized matches" : "Find your perfect treatment"}
          </h1>
          {!results && (
            <p className="text-[#5C6664] mt-3">Answer a few quick questions — it takes less than a minute.</p>
          )}
        </div>

        {/* progress */}
        <div className="h-1.5 bg-white rounded-full overflow-hidden mb-10" data-testid="quiz-progress">
          <div className="h-full iri-gradient transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {/* RESULTS */}
        {results ? (
          <div data-testid="quiz-results">
            <div className="grid sm:grid-cols-2 gap-5">
              {results.recommendations.map((r) => (
                <Link
                  to={`/services/${r.slug}`}
                  key={r.slug}
                  data-testid={`quiz-rec-${r.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover-lift block"
                >
                  <div className="img-zoom aspect-[16/10] overflow-hidden">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-heading text-lg font-medium text-ink">{r.name}</h3>
                      {r.price && <span className="text-xs text-iri-mid whitespace-nowrap">{r.price}</span>}
                    </div>
                    <p className="text-sm text-[#5C6664] mt-1.5">{r.short}</p>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-ink group-hover:gap-2.5 transition-all">
                      View treatment <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 iri-gradient rounded-3xl p-8 text-center">
              <h3 className="font-heading text-2xl font-light text-ink">Not sure where to start?</h3>
              <p className="text-ink/70 text-sm mt-2">Book a complimentary consultation and we'll build a plan just for you.</p>
              <div className="flex flex-wrap gap-3 justify-center mt-5">
                <Button onClick={() => openBooking()} data-testid="quiz-book-button" className="bg-ink text-white rounded-full h-12 px-8 hover:bg-ink/90">
                  Book a free consultation
                </Button>
                <Button onClick={restart} variant="outline" data-testid="quiz-restart-button" className="rounded-full h-12 px-8 border-ink/20 text-ink bg-white/40">
                  Retake the quiz
                </Button>
              </div>
            </div>

            <p className="text-xs text-[#8A9492] leading-relaxed mt-8 text-center max-w-2xl mx-auto" data-testid="quiz-disclaimer">
              These recommendations are for general informational purposes only and do not constitute medical advice.
              Results vary from person to person, and not every treatment is suitable for every individual.
              Suitability, candidacy and expected outcomes can only be determined during an in-person consultation
              at Iridescent Medical &amp; Wellness Spa. We've also emailed you a copy of your matches.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-100 p-7 md:p-10" data-testid="quiz-card">
            {/* STEP 0 — concern */}
            {step === 0 && (
              <div data-testid="quiz-step-concern">
                <h2 className="font-heading text-xl md:text-2xl font-light text-ink mb-6">What's your main concern?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {concerns.map((c) => (
                    <button
                      key={c}
                      onClick={() => pick("concern", c)}
                      data-testid={`quiz-concern-${c.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                      className={`text-left px-5 py-4 rounded-xl border text-sm transition-all ${
                        answers.concern === c ? "border-iri-mid bg-stone text-ink font-medium" : "border-slate-200 text-[#5C6664] hover:border-iri-mid/50 hover:bg-stone"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 1 — area */}
            {step === 1 && (
              <div data-testid="quiz-step-area">
                <h2 className="font-heading text-xl md:text-2xl font-light text-ink mb-6">Which area would you like to focus on?</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {AREAS.map((a) => (
                    <button
                      key={a.key}
                      onClick={() => pick("area", a.key)}
                      data-testid={`quiz-area-${a.key.toLowerCase()}`}
                      className={`text-left px-5 py-4 rounded-xl border transition-all ${
                        answers.area === a.key ? "border-iri-mid bg-stone" : "border-slate-200 hover:border-iri-mid/50 hover:bg-stone"
                      }`}
                    >
                      <p className="text-ink font-medium text-sm">{a.label}</p>
                      <p className="text-xs text-[#8A9492] mt-0.5">{a.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 — downtime */}
            {step === 2 && (
              <div data-testid="quiz-step-downtime">
                <h2 className="font-heading text-xl md:text-2xl font-light text-ink mb-6">How much downtime can you allow?</h2>
                <div className="space-y-3">
                  {DOWNTIMES.map((d) => (
                    <button
                      key={d.key}
                      onClick={() => pick("downtime", d.key)}
                      data-testid={`quiz-downtime-${d.key.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}
                      className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                        answers.downtime === d.key ? "border-iri-mid bg-stone" : "border-slate-200 hover:border-iri-mid/50 hover:bg-stone"
                      }`}
                    >
                      <p className="text-ink font-medium text-sm">{d.label}</p>
                      <p className="text-xs text-[#8A9492] mt-0.5">{d.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 3 — contact */}
            {step === 3 && (
              <form onSubmit={submit} data-testid="quiz-step-contact">
                <h2 className="font-heading text-xl md:text-2xl font-light text-ink mb-2">Where should we send your matches?</h2>
                <p className="text-sm text-[#5C6664] mb-6">We'll email your personalized recommendations and a member of our team will follow up.</p>
                <div className="space-y-4">
                  <Input required placeholder="Full name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="quiz-name-input" className="h-12 rounded-xl" />
                  <Input required type="email" placeholder="Email address" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="quiz-email-input" className="h-12 rounded-xl" />
                  <Input placeholder="Phone (optional)" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="quiz-phone-input" className="h-12 rounded-xl" />
                </div>
                <Button type="submit" disabled={submitting} data-testid="quiz-submit-button" className="mt-6 w-full iri-gradient text-ink rounded-full h-12 font-medium hover:scale-[1.02] transition-transform">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>See my matches <Check className="h-4 w-4 ml-2" /></>}
                </Button>
                <p className="text-[11px] text-[#8A9492] mt-4 text-center">By continuing you agree to be contacted about your recommendations. General information only — not medical advice.</p>
              </form>
            )}

            {/* back button */}
            {step > 0 && (
              <button onClick={() => setStep((s) => s - 1)} data-testid="quiz-back-button" className="mt-8 inline-flex items-center gap-2 text-sm text-[#8A9492] hover:text-ink transition-colors">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
